import { Component, AfterViewInit, inject, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { HttpService } from '../../../service/http/http.service';
import { environment } from '@environments/environment';
import { backendRestApis } from '@environments/backend-rest-apis';
import { GeoJsonFeatureCollection } from '../../../shared/models/heatmap/GeoJsonFeatureCollection';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-heatmap',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './heatmap.component.html',
  styleUrl: './heatmap.component.css',
})
export class HeatmapComponent implements AfterViewInit {

  @ViewChild('heatmapMap') mapContainer!: { nativeElement: HTMLElement };

  private httpService = inject(HttpService);
  private map!: L.Map;

  ngAfterViewInit(): void {
    this.initMap();
    this.loadHeatmapData();
  }

  private initMap(): void {
    this.map = L.map(this.mapContainer.nativeElement).setView([45.8130, 15.9770], 14);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      minZoom: 3,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);
  }

  private loadHeatmapData(): void {
    this.httpService
      .get<GeoJsonFeatureCollection>(
        `${environment.apiUrl}${backendRestApis.heatmap.getHeatmap}`
      )
      .subscribe({
        next: (data) => this.drawHeatmap(data),
        error: (error) => console.error('Neuspjelo dohvaćanje heatmap podataka:', error),
      });
  }

  private drawHeatmap(data: GeoJsonFeatureCollection): void {
    if (!data.features || data.features.length === 0) {
      return;
    }

    const maxVisitCount = Math.max(
      ...data.features.map((f) => f.properties.visitCount)
    );

    const layers: L.Polyline[] = [];

    for (const feature of data.features) {
      const latLngs: [number, number][] = feature.geometry.coordinates.map(
        ([lng, lat]) => [lat, lng] // GeoJSON je [lng, lat], Leaflet očekuje [lat, lng]
      );

      const color = this.getHeatmapColor(feature.properties.visitCount, maxVisitCount);

      const polyline = L.polyline(latLngs, {
        color,
        weight: 4,
        opacity: 0.8,
      }).addTo(this.map);

      layers.push(polyline);
    }

    const group = L.featureGroup(layers);
    this.map.fitBounds(group.getBounds());
  }

  /**
 * Vraća hex boju za dani broj posjeta, interpoliranu na log-skali
 * kroz plavu (najmanje posjećeno) do crvene (najposjećenije).
 */
 getHeatmapColor(visitCount: number, maxVisitCount: number): string {
  const logValue = Math.log(visitCount + 1);
  const logMax = Math.log(maxVisitCount + 1);
  const t = logMax === 0 ? 0 : Math.min(logValue / logMax, 1); // 0..1

  // Stopovi: plava -> cijan -> žuta -> crvena
  const stops: [number, [number, number, number]][] = [
    [0, [0, 0, 255]],     // plava
    [0.33, [0, 255, 255]], // cijan
    [0.66, [255, 255, 0]], // žuta
    [1, [255, 0, 0]],     // crvena
  ];

  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, c0] = stops[i];
    const [t1, c1] = stops[i + 1];
    if (t >= t0 && t <= t1) {
      const localT = (t - t0) / (t1 - t0);
      const r = Math.round(c0[0] + (c1[0] - c0[0]) * localT);
      const g = Math.round(c0[1] + (c1[1] - c0[1]) * localT);
      const b = Math.round(c0[2] + (c1[2] - c0[2]) * localT);
      return `rgb(${r}, ${g}, ${b})`;
    }
  }

  return 'rgb(255, 0, 0)';
}
}
